/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { useState } from 'react';
import cx from 'classnames';
import { ProductReviews } from '@/features/pdp/components/ProductReviews';
import { RebateOnlyBadge } from '@/features/pdp/components/RebateOnlyBadge';
import { SaveNowBadge } from '../SaveNowBadge';
import { Label } from '@/features/i18n';
import { useLocale } from '@/hooks/useLocale';
import Hidden from '@/components/Hidden';
import FitmentButton from '@/components/AZCustomComponent/FitmentButton';
import { ProductNote, ProductNotes } from '../ProductNotes';
import { ProductPrice } from '../ProductPrice';
import { ProductTitle } from '../ProductTitle';
import { ProductMetaLabel } from '@/components/ProductMetaLabel';
import CorePrice from '@/components/AZCustomComponent/CorePrice';
import viewStyles from '../viewStyles.module.scss';
import styles from './styles.module.scss';
import WarrantyBadge from '@/components/AZCustomComponent/WarrantyBadge';
import { useFeatureFlag } from '@/features/globalConfig';
import { useProductListView } from '../../../../../context/useProductListView';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import type { DealInfo } from '@/types/availability';
import useLatestCallback from 'use-latest-callback';
import { countryCodes } from '@/constants/locale';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { parseUrl } from '@/utils/urlHelpers';
import { ProductDeals } from '../ProductDeals';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { ProductCrossReference } from '../ProductCrossReference';
import { useProductList } from '../../../../../context/useProductList';
import { usePriceHiding } from '@/hooks/usePriceHiding';
import { setInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import { type VehicleFit } from '@/api/types/browse-search-types';
import ACESFitmentButton from '@/components/AZCustomComponent/ACESFitmentButton';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import {
  useReviewStatistics,
  useReviewStatisticsBatches,
} from '@/features/customReviews/api/getReviewStatistics';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { View } from '@az/starc-ui';
import { PurchasedDate } from '@/components/ActionCandidates/components/ProductCard/BuyAgainProduct/PurchasedDate';

type ProductAnalyticsData = {
  productIdList: string[];
  productBrandList: string[];
  productPartNumberList: string;
  originalPartTypeIdList?: string;
  productRepositoryIdList?: string;
  partGroupId?: string[];
  partTerminologyId?: string[];
};

export type ProductInfoProps = {
  application: string;
  cents: string;
  corePrice: number;
  dealInfo: DealInfo;
  dollars: string;
  fitsVehicle?: boolean;
  vehicleFitment?: VehicleFit;
  hasVehicle?: boolean;
  itemPresentInCart: boolean | undefined;
  notes: string;
  partNumber: string;
  productImage: string;
  productId: string | undefined;
  partGroupId?: string;
  partTerminologyId?: string;
  brandId?: string;
  positionId?: string;
  uniqueId?: number;
  partGroupName?: string;
  productName: string;
  recordType: string | undefined;
  showPriceCaption: boolean;
  showWarrantyModalWrapper: () => void;
  skuNumber: number;
  toggleShowYmme: (partGroupId?: string, partGroupName?: string) => void;
  url: string;
  warranty?: string;
  locationFilter?: string;
  storeRetailPrice?: string | number;
  productSpecs?: Record<string, string> | null;
  prodIndex: number;
  count: number;
  price: string;
  clearance: boolean | undefined;
  showDealDrawerButton: boolean;
  onDealClick: (ref: React.RefObject<HTMLButtonElement | null>) => void;
  isMobile: boolean;
  priceLoaded?: boolean;
  dealConfigurableLabel?: string | null | undefined;
  seoUrl?: string;
  partsThatFitUrl: string;
  oemBrandName?: string | undefined;
  oemPartNumber?: string | undefined;
  originalPartTypeId?: string | undefined;
  productBrandList?: string | undefined;
  onProductClick?: () => void;
  shippingType: string;
  setFulfillmentId?: (fulfillmentId: number | undefined) => void;
  handleQuantityChange: (a: number) => void;
  fulfillmentId?: number;
  isSearchPageType: boolean;
  renderProductAttributeChips?: React.ReactNode;
  productAttributeChipsEnabled: boolean;
  isSponsoredCard?: boolean;
  previouslyBoughtDate?: string;
};

const ProductInfo = ({
  application,
  cents,
  corePrice,
  dealInfo,
  dollars,
  fitsVehicle,
  vehicleFitment,
  hasVehicle,
  itemPresentInCart,
  notes,
  partNumber,
  partGroupId,
  partTerminologyId,
  brandId,
  positionId,
  uniqueId,
  partGroupName,
  productName,
  productImage,
  productId,
  recordType,
  showPriceCaption,
  showWarrantyModalWrapper,
  skuNumber,
  toggleShowYmme,
  url,
  warranty,
  locationFilter,
  storeRetailPrice,
  productSpecs,
  prodIndex,
  count,
  clearance,
  showDealDrawerButton,
  onDealClick,
  isMobile,
  price,
  priceLoaded = true,
  dealConfigurableLabel,
  partsThatFitUrl = '',
  oemBrandName,
  oemPartNumber,
  originalPartTypeId,
  productBrandList,
  onProductClick,
  handleQuantityChange,
  fulfillmentId,
  setFulfillmentId,
  shippingType,
  isSearchPageType,
  renderProductAttributeChips,
  productAttributeChipsEnabled,
  isSponsoredCard,
  previouslyBoughtDate,
}: ProductInfoProps) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { listViewState } = useProductListView();
  const { data: productListData, sponsoredSkus, pagedSkuIds } = useProductList();
  const sponsoredPartsAmount =
    productListData?.shelfParts?.filter((part) => !!part.isSponsoredCard).length ?? 0;
  const isListView = isBrandShelfPage(legacyRouter.asPath)
    ? listViewState.isBrandListView
    : listViewState.isListView;
  const [dealsModalOpen, setDealsModal] = useState(false);
  const shopByUrlYear = productListData?.shopByUrlYear;
  const shopByUrlMake = productListData?.shopByUrlMake;
  const shopByUrlModel = productListData?.shopByUrlModel;
  const makeOrModelName = productListData?.makeorModelName;
  const isDealBadgeEnabled = useForteDecisionFlagWithTrueDefault('dealBadgeEnabled');
  const { acesEnabled } = useAcesFlags();
  const hidePricing = usePriceHiding();

  const isShelfYMM = shopByUrlYear && shopByUrlMake && shopByUrlModel;
  const locale = useLocale();
  const { data: storeData } = useStoreDetailsData();
  const { setPageSpecificData } = usePageSpecificData();
  const isBopusEnabled = storeData?.bopusEnabled;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const index = prodIndex - sponsoredPartsAmount;
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const isMX = locale === countryCodes.mx;

  const { data: sponsoredReviewStatistics, isPending: sponsoredReviewsPending } =
    useReviewStatistics({
      skuNumbers: sponsoredSkus,
      enabled: isCustomReviewsEnabled && sponsoredSkus.length > 0,
    });

  const { combinedData: reviewStatistics, isPending: batchedReviewsPending } =
    useReviewStatisticsBatches({
      skuNumbers: pagedSkuIds,
      enabled: isCustomReviewsEnabled,
    });

  const reviewsPending =
    sponsoredSkus.length > 0
      ? sponsoredReviewsPending || batchedReviewsPending
      : batchedReviewsPending;

  const reviewStatistic = sponsoredReviewStatistics?.[skuNumber] ?? reviewStatistics[skuNumber];

  const productAnalyticsData: ProductAnalyticsData = {
    productIdList: [String(skuNumber)],
    productBrandList: [productBrandList ?? ''],
    productPartNumberList: partNumber,
    ...(acesEnabled
      ? {
          partGroupId: [partGroupId ?? ''],
          partTerminologyId: [partTerminologyId ?? ''],
        }
      : {
          originalPartTypeIdList: originalPartTypeId ?? '',
          productRepositoryIdList: productId ?? '',
        }),
  };

  const handleDealsModal = useLatestCallback(() => {
    setDealsModal(true);
  });

  const handleCloseModal = useLatestCallback(() => {
    setDealsModal(false);
  });

  const handleOpenYmmeFitment = (fitment?: VehicleFit) => {
    if (fitment === 'NEEDS_MORE_INFO') {
      setInteractionLocation('needsMoreInfo');
    } else if (hasVehicle && fitsVehicle) {
      setInteractionLocation('fitmentThisFits');
    } else if (hasVehicle && !fitsVehicle) {
      setInteractionLocation('fitmentThisDoesNotFit');
    } else {
      setInteractionLocation('fitmentCheckFits');
    }
    toggleShowYmme(partGroupId, partGroupName);
  };

  const onSeeFittingPartsClickHandler = () => {
    setPageSpecificData({
      productFindingMethod: 'Finder',
      pfmLocation: 'Product Fitment Banner',
      eventType: 'productFitmentBannerClick',
      finderFeature: 'Parts that Fit Product Card',
    });
    if (isSearchPageType) {
      void router.push(partsThatFitUrl);
    } else {
      void router.push(parseUrl(legacyRouter.asPath).url);
    }
  };
  const fitmentRender =
    recordType === 'app' || vehicleFitment === 'MAY_NOT_FIT' ? (
      <div className={cx(styles.fitmentButton, 'fitment-button-container')}>
        {acesEnabled && !!vehicleFitment ? (
          <ACESFitmentButton
            vehicleFitment={vehicleFitment}
            toggleShowYmme={handleOpenYmmeFitment}
            isList={isListView}
            productName={productName}
            onClickSeeFittingParts={onSeeFittingPartsClickHandler}
            makeModelYearPageType={!!productListData?.makeModelYearPageType}
            makeOrModelName={makeOrModelName}
          />
        ) : (
          <FitmentButton
            fitsVehicle={fitsVehicle}
            hasVehicle={hasVehicle}
            toggleShowYmme={handleOpenYmmeFitment}
            isList={isListView}
            makeOrModelName={makeOrModelName}
            onClickSeeFittingParts={onSeeFittingPartsClickHandler}
          />
        )}
      </div>
    ) : (
      <div className={cx('fitment-button-container')} />
    );
  const locationLabel = <Label label="label_cart_lineItem_Location" />;
  const partNumberLabel = (
    <span>
      <Label label="label_DetailsPage_body_PartNo" /> #
    </span>
  );
  const partNumberLabelBopusMX = (
    <span>
      <Label label="label_DetailsPage_body_PartNumber" />
      &nbsp;
    </span>
  );
  const skuNumberLabel = (
    <span>
      <Label label="Label_Sku_Uppercase" /> #
    </span>
  );
  return (
    <div className={cx({ [styles.productInfoContainerGrid]: !isListView })}>
      <Hidden smDown implementation="js">
        <div className={styles.grow}>
          <ProductTitle
            productName={productName}
            url={url}
            CustomElement={'h2'}
            onProductClick={onProductClick}
            prodIndex={index}
            partGroupId={partGroupId}
            brandId={brandId}
            positionId={positionId}
            uniqueId={uniqueId}
            isSearchPageType={isSearchPageType}
            isSponsoredCard={isSponsoredCard}
          />
        </div>
        <View className={cx('purchased-date')}>
          {previouslyBoughtDate ? (
            <View padding={[2, 0, 0, 0]}>
              <PurchasedDate purchasedDate={previouslyBoughtDate} />
            </View>
          ) : null}
        </View>
      </Hidden>
      {!hidePricing && (
        <Hidden mdUp implementation="js">
          <ProductPrice
            cents={cents}
            className={viewStyles.productInfoItemContainer}
            dollars={dollars}
            showPriceCaption={showPriceCaption}
            isDiscountShown={dealInfo.dealAvailable}
            itemPresentInCart={itemPresentInCart}
            prodIndex={prodIndex}
            count={count}
            price={price}
            clearance={clearance}
            showDealDrawerButton={showDealDrawerButton}
            onDealClick={onDealClick}
            isMobile={isMobile}
            priceLoaded={priceLoaded}
            fulfillmentId={fulfillmentId}
            shippingType={shippingType}
            setFulfillmentId={setFulfillmentId}
            handleQuantityChange={handleQuantityChange}
            dealConfigurableLabel={dealConfigurableLabel}
          />

          {isDealBadgeEnabled && <RebateOnlyBadge badgeDetails={dealInfo} />}
          {!isDealBadgeEnabled && (
            <SaveNowBadge badgeDetails={dealInfo} dealsCheck={handleDealsModal} />
          )}
          <ProductDeals
            dealsModalOpen={dealsModalOpen}
            skuNumber={skuNumber}
            productImage={productImage}
            productId={productId}
            productName={productName}
            handleCloseModal={handleCloseModal}
          />
          <CorePrice corePrice={corePrice} />
        </Hidden>
      )}
      <div
        className={cx(
          styles.productPartDetails,
          viewStyles.prodDetails,
          'product-detail-wrapper',
          styles.newFulfillmentDetails,
          { [styles.purchasedDateMargin]: isListView && !!previouslyBoughtDate },
        )}
      >
        <ul className={viewStyles.productNoteUnorderList}>
          {(!isListView || isMobile) &&
            (isShelfYMM || hasVehicle) &&
            !productAttributeChipsEnabled && (
              <li>
                <ProductNote
                  heading={locationLabel}
                  info={locationFilter}
                  application={application}
                  notes={notes}
                  partNumber={partNumber}
                  skuNumber={skuNumber}
                  warranty={warranty}
                  productName={productName}
                  storeRetailPrice={storeRetailPrice}
                  locationFilter={locationFilter}
                  productSpecs={productSpecs}
                  url={url}
                  onProductClick={onProductClick}
                  prodIndex={index}
                  partGroupId={partGroupId}
                  brandId={brandId}
                  positionId={positionId}
                  isSearchPageType={isSearchPageType}
                  isSponsoredCard={isSponsoredCard}
                />
              </li>
            )}
          <li
            className={cx(styles.productLabel, styles.partNumber, {
              [styles.inlineDesktop]: !isBopusMexicoStoreEnabled,
            })}
          >
            {partNumber && (
              <ProductMetaLabel
                label={isBopusMexicoStoreEnabled ? partNumberLabelBopusMX : partNumberLabel}
                value={`${partNumber}`}
                data-testid="product-part-number"
              />
            )}
          </li>
          <li
            className={cx(styles.productLabel, {
              [styles.inlineDesktop]: !isBopusMexicoStoreEnabled,
            })}
          >
            {skuNumber && (
              <ProductMetaLabel
                label={skuNumberLabel}
                value={`${skuNumber}`}
                data-testid="product-sku-number"
              />
            )}
          </li>
          {oemPartNumber && (
            <li className={styles.crossRef}>
              <ProductCrossReference oemBrandName={oemBrandName} oemPartNumber={oemPartNumber} />
            </li>
          )}
          <li>
            <WarrantyBadge
              warranty={warranty}
              onClick={showWarrantyModalWrapper}
              classes={{
                root: cx(styles.warranty, styles.newFulfillmentWarranty),
                button: styles.warrantyButton,
                label: styles.warrantyText,
              }}
            />
          </li>
          {locale !== countryCodes.ptBr && (!isMX || isCustomReviewsEnabled) && (
            <li>
              <ProductReviews
                skuNumber={skuNumber}
                reviewStatistic={reviewStatistic}
                reviewsPending={reviewsPending}
                productAnalyticsData={productAnalyticsData}
                url={url}
                onProductClick={onProductClick}
                isMobile={isMobile}
              />
            </li>
          )}
        </ul>
      </div>
      {!isMobile && <div className={cx(styles.fitmentBtnContainer)}>{fitmentRender}</div>}
      <ul>
        {isListView &&
          !isMobile &&
          !productAttributeChipsEnabled &&
          renderProductAttributeChips &&
          (isShelfYMM || hasVehicle) && (
            <li>
              <ProductNote
                heading={locationLabel}
                info={locationFilter}
                application={application}
                notes={notes}
                partNumber={partNumber}
                skuNumber={skuNumber}
                warranty={warranty}
                productName={productName}
                storeRetailPrice={storeRetailPrice}
                locationFilter={locationFilter}
                productSpecs={productSpecs}
                url={url}
                onProductClick={onProductClick}
                prodIndex={index}
                partGroupId={partGroupId}
                brandId={brandId}
                positionId={positionId}
                isSearchPageType={isSearchPageType}
                isSponsoredCard={isSponsoredCard}
              />
            </li>
          )}
        {isListView && !isMobile && productAttributeChipsEnabled && (
          <li>{renderProductAttributeChips}</li>
        )}
        {isListView &&
          (isShelfYMM || hasVehicle) && ( //IMPORTANT: Do not change to implementation css, SEO requested that this be completely hidden
            <Hidden implementation="js" smDown>
              <li>
                <div className={viewStyles.listViewNotes} id="notes">
                  <ProductNotes
                    application={application}
                    notes={notes}
                    partNumber={partNumber}
                    skuNumber={skuNumber}
                    warranty={warranty}
                    productName={productName}
                    storeRetailPrice={storeRetailPrice}
                    locationFilter={locationFilter}
                    productSpecs={productSpecs}
                    url={url}
                    onProductClick={onProductClick}
                    isShelfYMM={!!isShelfYMM}
                    isSearchPageType={isSearchPageType}
                  />
                </div>
              </li>
            </Hidden>
          )}
      </ul>
    </div>
  );
};

export { ProductInfo };
